import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Form, InputGroup, Button, Badge, Spinner, Alert, Pagination, Dropdown } from 'react-bootstrap';
import { FaSearch, FaFilter, FaEye, FaTrash, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import PageHeader from '../components/PageHeader';
import supportRequestService from '../services/supportRequestService';
import { useAuth } from '../context/AuthContext';

function SupportRequestsPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [supportRequests, setSupportRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [totalRequests, setTotalRequests] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const itemsPerPage = 10;

  const fetchSupportRequests = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: itemsPerPage,
        sort_by: sortField,
        sort_order: sortDirection,
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      if (selectedStatus) {
        params.status = selectedStatus;
      }

      if (selectedPriority) {
        params.priority = selectedPriority;
      }

      const data = auth.isAdmin ? await supportRequestService.getAdminAll(params) : await supportRequestService.getAll(params);
      setSupportRequests(data.support_requests);
      setTotalRequests(data.total);
      setTotalPages(data.pages);
    } catch (err) {
      setError('Failed to load support requests');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, sortField, sortDirection, searchQuery, selectedStatus, selectedPriority, auth.isAdmin]);

  useEffect(() => {
    fetchSupportRequests();
  }, [fetchSupportRequests]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this support request?')) {
      try {
        await supportRequestService.delete(id);
        fetchSupportRequests();
      } catch (err) {
        setError('Failed to delete support request');
        console.error(err);
      }
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await supportRequestService.updateStatus(id, newStatus);
      fetchSupportRequests();
    } catch (err) {
      setError('Failed to update status');
      console.error(err);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort className="text-muted" size={12} />;
    return sortDirection === 'asc' ? <FaSortUp size={12} /> : <FaSortDown size={12} />;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'open': return 'danger';
      case 'in_progress': return 'secondary';
      case 'resolved': return 'success';
      case 'closed': return 'secondary';
      default: return 'secondary';
    }
  };

  const getPriorityBadgeVariant = (priority) => {
    switch (priority) {
      case 'urgent': return 'danger';
      case 'high': return 'warning';
      case 'normal': return 'info';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="px-4">
      <PageHeader
        title="Support Requests"
        subtitle={`${totalRequests} support requests`}
      />

      <Row className="g-3 mb-4 align-items-center">
        <Col md={5}>
          <InputGroup>
            <InputGroup.Text className="bg-white">
              <FaSearch className="text-muted" />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search support requests..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </InputGroup>
        </Col>
        <Col md={3}>
          <InputGroup>
            <InputGroup.Text className="bg-white">
              <FaFilter className="text-muted" />
            </InputGroup.Text>
            <Form.Select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Statuses</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </Form.Select>
          </InputGroup>
        </Col>
        <Col md={2}>
          <Form.Select
            value={selectedPriority}
            onChange={(e) => {
              setSelectedPriority(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Button
            variant="primary"
            className="w-100"
            onClick={() => navigate('/support-requests/new')}
          >
            New Request
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card className="border-0 shadow-sm" style={{ overflow: 'visible', position: 'relative', zIndex: 1 }}>
        <Card.Body className="p-0">
          <div className="table-responsive" style={{ overflow: 'visible' }}>
            <Table hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="fw-semibold" style={{ cursor: 'pointer' }} onClick={() => handleSort('id')}>
                    {getSortIcon('id')} ID
                  </th>
                  <th className="fw-semibold">Order</th>
                  <th className="fw-semibold">User</th>
                  <th className="fw-semibold" style={{ cursor: 'pointer' }} onClick={() => handleSort('subject')}>
                    {getSortIcon('subject')} Subject
                  </th>
                  <th className="fw-semibold" style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>
                    {getSortIcon('status')} Status
                  </th>
                  <th className="fw-semibold" style={{ cursor: 'pointer' }} onClick={() => handleSort('priority')}>
                    {getSortIcon('priority')} Priority
                  </th>
                  <th className="fw-semibold" style={{ cursor: 'pointer' }} onClick={() => handleSort('created_at')}>
                    {getSortIcon('created_at')} Created
                  </th>
                  <th className="fw-semibold text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {supportRequests.map((request) => (
                  <tr key={request.id}>
                    <td>
                      <span className="fw-semibold">#{request.id}</span>
                    </td>
                    <td>
                      <Badge bg="light" text="dark" pill>
                        Order #{request.order_number}
                      </Badge>
                    </td>
                    <td>
                      <div className="text-truncate" style={{ maxWidth: '200px' }}>
                        {request.user_email}
                      </div>
                    </td>
                    <td>
                      <div className="text-truncate" style={{ maxWidth: '250px' }} title={request.subject}>
                        {request.subject}
                      </div>
                    </td>
                    <td style={{ position: 'relative' }}>
                      <Dropdown onSelect={(status) => handleStatusUpdate(request.id, status)}>
                        <Dropdown.Toggle
                          variant={getStatusBadgeVariant(request.status)}
                          size="sm"
                          className="text-capitalize"
                        >
                          {request.status.replace('_', ' ')}
                        </Dropdown.Toggle>
                        <Dropdown.Menu style={{ zIndex: 1050 }}>
                          <Dropdown.Item eventKey="open">Open</Dropdown.Item>
                          <Dropdown.Item eventKey="in_progress">In Progress</Dropdown.Item>
                          <Dropdown.Item eventKey="resolved">Resolved</Dropdown.Item>
                          <Dropdown.Item eventKey="closed">Closed</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                    <td>
                      <Badge bg={getPriorityBadgeVariant(request.priority)} className="text-capitalize">
                        {request.priority}
                      </Badge>
                    </td>
                    <td>
                      <small className="text-muted">
                        {formatDate(request.created_at)}
                      </small>
                    </td>
                    <td className="text-end">
                      <Button
                        variant="link"
                        size="sm"
                        className="text-dark p-1"
                        onClick={() => navigate(`/support-requests/${request.id}`)}
                      >
                        <FaEye />
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-dark p-1 ms-2"
                        onClick={() => handleDelete(request.id)}
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />

            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Pagination.Item>
                );
              } else if (page === currentPage - 2 || page === currentPage + 2) {
                return <Pagination.Ellipsis key={page} disabled />;
              }
              return null;
            })}

            <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
            <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
          </Pagination>
        </div>
      )}
    </Container>
  );
}

export default SupportRequestsPage;